import type { StoredSettings } from '@/components/reporting/reportColumns';

/**
 * Scheduled reports: which report, its filters, the period it covers and when it is emailed.
 * Shared by the Reports page and the server, which builds and sends them.
 * Times and dates are the server's local time (South African time on this VPS).
 */

export const REPORT_KINDS = [
  'activity',
  'missedAlert',
  'summary',
  'detail',
  'costs',
  'missed',
  'queueMissed',
  'failover',
] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

export const REPORT_KIND_LABELS: Record<ReportKind, string> = {
  activity: 'Daily Activity',
  missedAlert: 'Unreturned missed call alert',
  summary: 'Call Summary',
  detail: 'Call Detail',
  costs: 'Call Costs',
  missed: 'Unreturned Missed',
  queueMissed: 'Queue Missed',
  failover: 'Failover Follow-through',
};

export const DATE_RANGES = [
  'yesterday',
  'today',
  'last7Days',
  'thisWeek',
  'lastWeek',
  'last30Days',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'lastYear',
] as const;
export type DateRangeKey = (typeof DATE_RANGES)[number];

export const DATE_RANGE_LABELS: Record<DateRangeKey, string> = {
  yesterday: 'Yesterday',
  today: 'Today so far',
  last7Days: 'Last 7 days',
  thisWeek: 'This week so far',
  lastWeek: 'Last week (Mon–Sun)',
  last30Days: 'Last 30 days',
  thisMonth: 'This month so far',
  lastMonth: 'Last month',
  thisYear: 'This year so far',
  lastYear: 'Last year',
};

/** "interval" is for alerts: a check every few minutes between `time` and `endTime`. */
export const FREQUENCIES = ['interval', 'daily', 'weekly', 'monthly'] as const;
export type Frequency = (typeof FREQUENCIES)[number];

/** How often an alert can check, in minutes. */
export const CHECK_INTERVALS = [15, 30, 60, 120, 240] as const;
export const DEFAULT_CHECK_INTERVAL = 60;

export const REPORT_FORMATS = ['pdf', 'csv'] as const;
export type ReportFormat = (typeof REPORT_FORMATS)[number];

/** Monday first, as the PBX reports count weeks. JS day numbers (0 = Sunday). */
export const WEEKDAYS = [
  { day: 1, label: 'Monday' },
  { day: 2, label: 'Tuesday' },
  { day: 3, label: 'Wednesday' },
  { day: 4, label: 'Thursday' },
  { day: 5, label: 'Friday' },
  { day: 6, label: 'Saturday' },
  { day: 0, label: 'Sunday' },
] as const;

export const MAX_RECIPIENTS = 10;
export const DEFAULT_INLINE_ROW_LIMIT = 20;
export const MAX_INLINE_ROW_LIMIT = 200;
export const MAX_SCHEDULES_PER_CUSTOMER = 50;

export interface ScheduleTiming {
  frequency: Frequency;
  /** "07:00", 24-hour. Interval: the first check of the day. */
  time: string;
  /** Interval: no checks after this time, e.g. "18:00". */
  endTime?: string;
  /** Interval: minutes between checks. */
  intervalMinutes?: number;
  /** Weekly: the day, 0 = Sunday … 6 = Saturday. */
  weekday?: number;
  /** Monthly: 1–28, so every month has it. */
  dayOfMonth?: number;
  /** Daily and interval: Monday to Friday only. */
  weekdaysOnly?: boolean;
}

export interface ActivityFilters {
  mobileRate: number;
  landlineRate: number;
  /** Each extension's calls made, one by one, after the totals. */
  includeCallLists: boolean;
}

export interface MissedAlertFilters {
  /** A missed call is reported once it has gone this long without being returned. */
  thresholdMinutes: number;
}

/** Alerts are checks that email only when they find something; the rest are reports. */
export function isAlertKind(kind: ReportKind) {
  return kind === 'missedAlert';
}

export interface SummaryFilters {
  /** Extension number. */
  extension: string;
}

export type DetailFilters = SummaryFilters;

export interface CostsFilters {
  /** Extension number, or "all". */
  extension: string;
  callFilter: 'outbound' | 'all';
  mobileRate: number;
  landlineRate: number;
  /** Column choices copied from the Call Costs page. */
  callColumns?: StoredSettings;
  extensionColumns?: StoredSettings;
}

export interface MissedFilters {
  callFrom?: string;
  callTo?: string;
  /** extension, queue or ring_group. */
  callToType?: string;
  /** no_answer, busy or abandoned. */
  missCallType?: string;
  /** 1 returned, 2 unreturned; left out for both. */
  callbackStatus?: 1 | 2;
  abandonTime?: number;
}

export interface QueueMissedFilters {
  /** Empty: every queue, including ones added later. */
  queueIds: string[];
  statusFilter: 'missed' | 'all';
  callFrom?: string;
  abandonTime?: number;
}

export interface FailoverFilters {
  /** Empty: every queue. */
  queueIds: string[];
  /** A failover outcome, or "all". */
  outcome: string;
  search?: string;
}

export interface ReportFiltersByKind {
  activity: ActivityFilters;
  missedAlert: MissedAlertFilters;
  summary: SummaryFilters;
  detail: DetailFilters;
  costs: CostsFilters;
  missed: MissedFilters;
  queueMissed: QueueMissedFilters;
  failover: FailoverFilters;
}

export type ReportFilters = ReportFiltersByKind[ReportKind];

/** The parts of a schedule a person chooses (the rest is the server's bookkeeping). */
export interface ScheduleInput extends ScheduleTiming {
  name: string;
  reportKind: ReportKind;
  filters: ReportFilters;
  dateRange: DateRangeKey;
  recipients: string[];
  /** Files to attach when they are attached. */
  formats: ReportFormat[];
  /** Detailed lists with up to this many rows are shown in the email itself; 0 never. */
  inlineRowLimit: number;
  /** Attach the files every time, not only when the details are too long for the email. */
  attachAlways: boolean;
  enabled: boolean;
}

export interface ScheduledReport extends ScheduleInput {
  _id: string;
  customerId: string;
  nextRunAtMs: number | null;
  lastRunAtMs?: number;
  /** "nothing" when an alert found nothing to send. */
  lastStatus?: 'sent' | 'failed' | 'nothing';
  lastMessage?: string;
  ownerName?: string;
  ownerEmail?: string;
  updatedAt: string;
}

/** The date range a report covers, decided when it is sent. */
export interface ReportPeriod {
  /** YYYY-MM-DD, inclusive. */
  startDate: string;
  endDate: string;
}

export function formatIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const addDays = (date: Date, days: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

export function resolveDateRange(key: DateRangeKey, now: Date): ReportPeriod {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysSinceMonday = (today.getDay() + 6) % 7;
  const range = (start: Date, end: Date) => ({ startDate: formatIsoDate(start), endDate: formatIsoDate(end) });
  switch (key) {
    case 'today':
      return range(today, today);
    case 'last7Days':
      return range(addDays(today, -7), addDays(today, -1));
    case 'thisWeek':
      return range(addDays(today, -daysSinceMonday), today);
    case 'lastWeek':
      return range(addDays(today, -daysSinceMonday - 7), addDays(today, -daysSinceMonday - 1));
    case 'last30Days':
      return range(addDays(today, -30), addDays(today, -1));
    case 'thisMonth':
      return range(new Date(today.getFullYear(), today.getMonth(), 1), today);
    case 'lastMonth':
      return range(
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        new Date(today.getFullYear(), today.getMonth(), 0)
      );
    case 'thisYear':
      return range(new Date(today.getFullYear(), 0, 1), today);
    case 'lastYear':
      return range(new Date(today.getFullYear() - 1, 0, 1), new Date(today.getFullYear() - 1, 11, 31));
    case 'yesterday':
    default:
      return range(addDays(today, -1), addDays(today, -1));
  }
}

/** The usual period for each frequency: the day, week or month that has just ended. */
export function defaultDateRange(frequency: Frequency): DateRangeKey {
  return frequency === 'monthly' ? 'lastMonth' : frequency === 'weekly' ? 'lastWeek' : 'yesterday';
}

export const DEFAULT_THRESHOLD_MINUTES = 15;

function parseTime(time: string): { hours: number; minutes: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours < 24 && minutes < 60 ? { hours, minutes } : null;
}

function runsOn(timing: ScheduleTiming, day: Date) {
  if (timing.frequency === 'weekly') return day.getDay() === (timing.weekday ?? 1);
  if (timing.frequency === 'monthly') return day.getDate() === (timing.dayOfMonth ?? 1);
  return !timing.weekdaysOnly || (day.getDay() !== 0 && day.getDay() !== 6);
}

/** Interval: every `intervalMinutes` from `time` up to `endTime` (inclusive), on the days it runs. */
function nextIntervalRun(timing: ScheduleTiming, after: Date): number {
  const first = parseTime(timing.time) ?? { hours: 7, minutes: 0 };
  const last = parseTime(timing.endTime ?? '') ?? { hours: 18, minutes: 0 };
  const step = Math.max(5, timing.intervalMinutes ?? DEFAULT_CHECK_INTERVAL);
  const firstMinute = first.hours * 60 + first.minutes;
  const lastMinute = Math.max(firstMinute, last.hours * 60 + last.minutes);
  for (let offset = 0; offset <= 8; offset += 1) {
    const day = addDays(after, offset);
    if (!runsOn({ ...timing, frequency: 'daily' }, day)) continue;
    for (let minute = firstMinute; minute <= lastMinute; minute += step) {
      const candidate = new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(minute / 60), minute % 60);
      if (candidate.getTime() > after.getTime()) return candidate.getTime();
    }
  }
  return addDays(after, 1).getTime();
}

/** The first send (or check) time after `after`, in milliseconds. */
export function nextRunAfter(timing: ScheduleTiming, after: Date): number {
  if (timing.frequency === 'interval') return nextIntervalRun(timing, after);
  const time = parseTime(timing.time) ?? { hours: 7, minutes: 0 };
  for (let offset = 0; offset <= 62; offset += 1) {
    const day = addDays(after, offset);
    const candidate = new Date(day.getFullYear(), day.getMonth(), day.getDate(), time.hours, time.minutes);
    if (candidate.getTime() > after.getTime() && runsOn(timing, candidate)) return candidate.getTime();
  }
  // Unreachable for valid timings (a month never has more than 62 days between matches)
  return addDays(after, 1).getTime();
}

const ordinal = (n: number) => {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};

/** "Every Monday at 07:00" */
export function describeTiming(timing: ScheduleTiming): string {
  if (timing.frequency === 'interval') {
    const minutes = timing.intervalMinutes ?? DEFAULT_CHECK_INTERVAL;
    const every = minutes % 60 === 0 ? (minutes === 60 ? 'Every hour' : `Every ${minutes / 60} hours`) : `Every ${minutes} minutes`;
    return `${every}, ${timing.time}–${timing.endTime ?? '18:00'}${timing.weekdaysOnly ? ' on weekdays' : ''}`;
  }
  if (timing.frequency === 'weekly') {
    const day = WEEKDAYS.find((item) => item.day === (timing.weekday ?? 1))?.label ?? 'Monday';
    return `Every ${day} at ${timing.time}`;
  }
  if (timing.frequency === 'monthly') return `Monthly on the ${ordinal(timing.dayOfMonth ?? 1)} at ${timing.time}`;
  return `${timing.weekdaysOnly ? 'Weekdays' : 'Every day'} at ${timing.time}`;
}

const EMAIL_PATTERN = /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]+$/;

export function isValidEmail(value: string) {
  return value.length <= 254 && EMAIL_PATTERN.test(value);
}

/** Addresses from a typed list (commas, semicolons, spaces or new lines), lower-cased and without repeats. */
export function parseRecipients(text: string): { valid: string[]; invalid: string[] } {
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const part of text.split(/[\s,;]+/)) {
    const value = part.trim().replace(/^<|>$/g, '').toLowerCase();
    if (!value) continue;
    if (!isValidEmail(value)) invalid.push(part.trim());
    else if (!valid.includes(value)) valid.push(value);
  }
  return { valid, invalid };
}

const cleanText = (value: unknown, max = 80) => (typeof value === 'string' ? value.trim().slice(0, max) : '');
const optionalText = (value: unknown, max = 80) => cleanText(value, max) || undefined;
const optionalNumber = (value: unknown, min: number, max: number) => {
  const number = Number(value);
  return value === undefined || value === null || value === '' || !Number.isFinite(number)
    ? undefined
    : Math.min(max, Math.max(min, number));
};
const idList = (value: unknown) =>
  Array.isArray(value)
    ? Array.from(new Set(value.map((id) => cleanText(String(id ?? ''), 40)).filter(Boolean))).slice(0, 200)
    : [];

function cleanColumns(value: unknown): StoredSettings | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as StoredSettings;
  const id = (text: string) => /^[A-Za-z0-9_-]{1,40}$/.test(text);
  const visible = Object.fromEntries(
    Object.entries(raw.visible ?? {}).filter(([key, shown]) => id(key) && typeof shown === 'boolean')
  );
  const widths = Object.fromEntries(
    Object.entries(raw.widths ?? {})
      .filter(([key, width]) => id(key) && Number.isFinite(width))
      .map(([key, width]) => [key, Math.round(Math.min(2000, Math.max(20, width)))])
  );
  const sort =
    raw.sort && id(String(raw.sort.id)) && (raw.sort.dir === 'asc' || raw.sort.dir === 'desc')
      ? { id: String(raw.sort.id), dir: raw.sort.dir }
      : undefined;
  return { visible, widths, ...(sort ? { sort } : {}) };
}

/** Filters as the server stores them: known fields only, trimmed and in range. Throws when one is missing. */
export function normalizeFilters<K extends ReportKind>(kind: K, raw: unknown): ReportFiltersByKind[K] {
  const input = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const extension = cleanText(input.extension, 40);
  switch (kind) {
    case 'activity':
      return {
        mobileRate: optionalNumber(input.mobileRate, 0, 100) ?? 0.6,
        landlineRate: optionalNumber(input.landlineRate, 0, 100) ?? 0.3,
        includeCallLists: input.includeCallLists !== false,
      } as ReportFiltersByKind[K];
    case 'missedAlert':
      return {
        thresholdMinutes: Math.round(optionalNumber(input.thresholdMinutes, 1, 600) ?? DEFAULT_THRESHOLD_MINUTES),
      } as ReportFiltersByKind[K];
    case 'summary':
    case 'detail':
      if (!extension || extension === 'all') throw new Error('Choose an extension for this report');
      return { extension } as ReportFiltersByKind[K];
    case 'costs':
      return {
        extension: extension || 'all',
        callFilter: input.callFilter === 'all' ? 'all' : 'outbound',
        mobileRate: optionalNumber(input.mobileRate, 0, 100) ?? 0.6,
        landlineRate: optionalNumber(input.landlineRate, 0, 100) ?? 0.3,
        callColumns: cleanColumns(input.callColumns),
        extensionColumns: cleanColumns(input.extensionColumns),
      } as ReportFiltersByKind[K];
    case 'missed': {
      const status = Number(input.callbackStatus);
      return {
        callFrom: optionalText(input.callFrom),
        callTo: optionalText(input.callTo),
        callToType: ['extension', 'queue', 'ring_group'].includes(String(input.callToType))
          ? String(input.callToType)
          : undefined,
        missCallType: ['no_answer', 'busy', 'abandoned'].includes(String(input.missCallType))
          ? String(input.missCallType)
          : undefined,
        callbackStatus: status === 1 || status === 2 ? status : undefined,
        abandonTime: optionalNumber(input.abandonTime, 0, 600),
      } as ReportFiltersByKind[K];
    }
    case 'queueMissed':
      return {
        queueIds: idList(input.queueIds),
        statusFilter: input.statusFilter === 'all' ? 'all' : 'missed',
        callFrom: optionalText(input.callFrom),
        abandonTime: optionalNumber(input.abandonTime, 0, 600),
      } as ReportFiltersByKind[K];
    case 'failover':
      return {
        queueIds: idList(input.queueIds),
        outcome: cleanText(input.outcome, 40) || 'all',
        search: optionalText(input.search),
      } as ReportFiltersByKind[K];
    default:
      throw new Error('Unknown report');
  }
}

export interface FilterNames {
  extensions?: Map<string, string>;
  queues?: Map<string, string>;
}

const MISSED_TYPE_LABELS: Record<string, string> = { no_answer: 'No answer', busy: 'Busy', abandoned: 'Abandoned' };
const DESTINATION_LABELS: Record<string, string> = { extension: 'Extension', queue: 'Queue', ring_group: 'Ring group' };

function queueList(ids: string[], names?: Map<string, string>) {
  if (ids.length === 0) return 'All queues';
  const shown = ids.slice(0, 4).map((id) => names?.get(id) ?? `Queue ${id}`);
  return `${shown.join(', ')}${ids.length > 4 ? ` and ${ids.length - 4} more` : ''}`;
}

/** Short descriptions of the filters, e.g. ["All extensions", "Outbound calls"]. */
export function describeFilters(kind: ReportKind, filters: ReportFilters, names: FilterNames = {}): string[] {
  const extensionLabel = (number: string) =>
    number === 'all' ? 'All extensions' : `Extension ${number}${names.extensions?.get(number) ? ` (${names.extensions.get(number)})` : ''}`;
  switch (kind) {
    case 'activity': {
      const f = filters as ActivityFilters;
      return [
        'All extensions',
        `Mobile R${f.mobileRate.toFixed(2)}/min, landline R${f.landlineRate.toFixed(2)}/min`,
        f.includeCallLists ? 'With each extension’s calls made' : 'Totals per extension only',
      ];
    }
    case 'missedAlert':
      return [`Missed calls not returned within ${(filters as MissedAlertFilters).thresholdMinutes} minutes`];
    case 'summary':
    case 'detail':
      return [extensionLabel((filters as SummaryFilters).extension)];
    case 'costs': {
      const f = filters as CostsFilters;
      return [
        extensionLabel(f.extension),
        f.callFilter === 'all' ? 'All calls listed' : 'Outbound calls listed',
        `Mobile R${f.mobileRate.toFixed(2)}/min, landline R${f.landlineRate.toFixed(2)}/min`,
      ];
    }
    case 'missed': {
      const f = filters as MissedFilters;
      return [
        f.callbackStatus === 2 ? 'Unreturned only' : f.callbackStatus === 1 ? 'Returned only' : 'Returned and unreturned',
        f.callToType ? `To: ${DESTINATION_LABELS[f.callToType] ?? f.callToType}` : '',
        f.missCallType ? `Type: ${MISSED_TYPE_LABELS[f.missCallType] ?? f.missCallType}` : '',
        f.callFrom ? `From contains "${f.callFrom}"` : '',
        f.callTo ? `To contains "${f.callTo}"` : '',
      ].filter(Boolean);
    }
    case 'queueMissed': {
      const f = filters as QueueMissedFilters;
      return [
        queueList(f.queueIds, names.queues),
        f.statusFilter === 'all' ? 'All queue calls' : 'Missed and abandoned only',
        f.callFrom ? `Caller contains "${f.callFrom}"` : '',
      ].filter(Boolean);
    }
    case 'failover': {
      const f = filters as FailoverFilters;
      return [
        queueList(f.queueIds, names.queues),
        f.outcome && f.outcome !== 'all' ? `Outcome: ${f.outcome.replace(/_/g, ' ')}` : '',
        f.search ? `Matching "${f.search}"` : '',
      ].filter(Boolean);
    }
    default:
      return [];
  }
}

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** A schedule as typed in, checked and cleaned; throws with a message for the person when something is wrong. */
export function normalizeScheduleInput(raw: unknown): ScheduleInput {
  const input = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const reportKind = String(input.reportKind) as ReportKind;
  if (!REPORT_KINDS.includes(reportKind)) throw new Error('Choose a report');
  const alert = isAlertKind(reportKind);

  const frequency = (alert ? 'interval' : String(input.frequency)) as Frequency;
  if (!FREQUENCIES.includes(frequency) || (!alert && frequency === 'interval')) throw new Error('Choose how often to send it');
  const time = String(input.time ?? '').trim();
  if (!TIME_PATTERN.test(time)) throw new Error('Enter a time like 07:00');

  const timing: ScheduleTiming = { frequency, time };
  if (frequency === 'interval') {
    const endTime = String(input.endTime ?? '').trim();
    if (!TIME_PATTERN.test(endTime)) throw new Error('Enter the last check time, like 18:00');
    if (endTime < time) throw new Error('The last check must be after the first');
    const interval = Number(input.intervalMinutes);
    timing.endTime = endTime;
    timing.intervalMinutes = (CHECK_INTERVALS as readonly number[]).includes(interval) ? interval : DEFAULT_CHECK_INTERVAL;
    timing.weekdaysOnly = input.weekdaysOnly === true;
  } else if (frequency === 'weekly') {
    const weekday = Number(input.weekday);
    timing.weekday = Number.isInteger(weekday) && weekday >= 0 && weekday <= 6 ? weekday : 1;
  } else if (frequency === 'monthly') {
    const day = Number(input.dayOfMonth);
    timing.dayOfMonth = Number.isInteger(day) && day >= 1 && day <= 28 ? day : 1;
  } else {
    timing.weekdaysOnly = input.weekdaysOnly === true;
  }

  const recipientList = Array.isArray(input.recipients) ? input.recipients.map(String).join(',') : String(input.recipients ?? '');
  const { valid, invalid } = parseRecipients(recipientList);
  if (invalid.length) throw new Error(`Not an email address: ${invalid.slice(0, 3).join(', ')}`);
  if (valid.length === 0) throw new Error('Add at least one email address');
  if (valid.length > MAX_RECIPIENTS) throw new Error(`Up to ${MAX_RECIPIENTS} email addresses`);

  const formats = alert
    ? []
    : REPORT_FORMATS.filter((format) => Array.isArray(input.formats) && input.formats.includes(format));
  if (!alert && formats.length === 0) throw new Error('Choose PDF, CSV or both');

  const dateRange = String(input.dateRange) as DateRangeKey;
  return {
    ...timing,
    name: cleanText(input.name, 80) || REPORT_KIND_LABELS[reportKind],
    reportKind,
    filters: normalizeFilters(reportKind, input.filters),
    dateRange: alert ? 'today' : DATE_RANGES.includes(dateRange) ? dateRange : defaultDateRange(frequency),
    recipients: valid,
    formats,
    inlineRowLimit: Math.round(
      Math.min(MAX_INLINE_ROW_LIMIT, Math.max(0, Number.isFinite(Number(input.inlineRowLimit)) ? Number(input.inlineRowLimit) : DEFAULT_INLINE_ROW_LIMIT))
    ),
    attachAlways: input.attachAlways === true,
    enabled: input.enabled !== false,
  };
}

/** A new schedule's starting point for each report: the presets on Reports → Scheduled. */
export function scheduleDefaults(kind: ReportKind): Omit<ScheduleInput, 'recipients'> {
  const base = {
    name: REPORT_KIND_LABELS[kind],
    reportKind: kind,
    enabled: true,
    formats: ['pdf'] as ReportFormat[],
    inlineRowLimit: DEFAULT_INLINE_ROW_LIMIT,
    attachAlways: false,
  };
  switch (kind) {
    case 'activity':
      return {
        ...base,
        filters: { mobileRate: 0.6, landlineRate: 0.3, includeCallLists: true },
        dateRange: 'today',
        frequency: 'daily',
        time: '18:00',
      };
    case 'missedAlert':
      return {
        ...base,
        name: 'Unreturned missed calls',
        filters: { thresholdMinutes: DEFAULT_THRESHOLD_MINUTES },
        dateRange: 'today',
        frequency: 'interval',
        time: '07:00',
        endTime: '18:00',
        intervalMinutes: DEFAULT_CHECK_INTERVAL,
        formats: [],
      };
    case 'costs':
      return {
        ...base,
        filters: { extension: 'all', callFilter: 'outbound', mobileRate: 0.6, landlineRate: 0.3 },
        dateRange: 'lastMonth',
        frequency: 'monthly',
        dayOfMonth: 1,
        time: '07:00',
        formats: ['pdf', 'csv'],
      };
    case 'summary':
      return { ...base, filters: { extension: '' }, dateRange: 'lastMonth', frequency: 'monthly', dayOfMonth: 1, time: '07:00' };
    case 'detail':
      return { ...base, filters: { extension: '' }, dateRange: 'lastWeek', frequency: 'weekly', weekday: 1, time: '07:00' };
    case 'missed':
      return { ...base, filters: { callbackStatus: 2, abandonTime: 5 }, dateRange: 'yesterday', frequency: 'daily', time: '07:00' };
    case 'queueMissed':
      return { ...base, filters: { queueIds: [], statusFilter: 'missed', abandonTime: 5 }, dateRange: 'yesterday', frequency: 'daily', time: '07:00' };
    case 'failover':
    default:
      return { ...base, filters: { queueIds: [], outcome: 'all' }, dateRange: 'yesterday', frequency: 'daily', time: '07:00' };
  }
}
